import React, { memo, useCallback, useEffect, useState } from 'react';
import styles from '@/styles/pages/category.module.scss';
import LatestArticle from '@/components/common/latest-article';
import { Pagination, Breadcrumb } from 'antd';
import type { PaginationProps } from 'antd';
import { BreadcrumbList } from 'schema-dts';
import { jsonLdScriptProps } from 'react-schemaorg';
import Head from 'next/head';
import SortFilter from '@/components/common/sort-filter';
import { getCategoryPageAction, getCateTagsAction, getProductTagsAction } from '@/store/actions/categoryPageAction';
import { AppState, wrapper } from '@/store';
import { ServerContext } from '@/type/pages';
import LazyImage from '@/components/common/lazy-image';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import NProgress from 'nprogress';
import {
  changeCategoryId,
  changeIsOpen,
  changeTopicSelected,
  changeSortBy,
  changePage,
  changePageSize,
  changeStartTimestamp,
  changeEndTimestamp,
  changeProductSelected,
} from '@/store/slices/sortFilterSlice';
import Image from 'next/image';
import { resetSortFilter } from '@/store/actions/sortFilterAction';
import classNames from 'classnames';
import NextImage from 'next/image';
import placeholder from '@/public/img/PlaceholderImage.png';
import IconPageLeft from '@/public/icon/Left.svg';
import IconPageRight from '@/public/icon/Right.svg';
import { filterSvgPath } from '@/utils/dataFormat';
import {
  getAllCategory,
  getAllCategoryLanding,
  getCategoryPage,
  getCateTags,
  getProductTags,
} from '@/services/category';
import { getEntryByContentTypeUrl, getMultiplePopupBanner, getPromotionBanner } from '@/services/contentstack';
import { getHeaderAndFooter } from '@/services/headerAndFooter';
import { HEADER_FOOTER_CONTENT_TYPE } from '@/constants/headerAndFooter';
import { headerAndFooterIncludeReference, HOME_PAGE_CONTENT_TYPE, HOME_PAGE_URL } from '@/constants/homePage';
import {
  changeAllCategory,
  changeAllCategoryLanding,
  changeCategoryPage,
  changeCateTags,
  changeProductTags,
} from '@/store/slices/categoryPageSlice';
import { changeDefaultData } from '@/pages/blog/index';
import { useRouter } from 'next/router';
import { useTranslation } from '@/hooks/use-translation';
import { error, info } from '@/utils/safetyLog';
export default memo(({ page, category, language }: any) => {
  info('blog/category memo page:', page);
  info('blog/category memo category:', category);
  info('blog/category memo language:', language);
  const dispatch = useDispatch<any>();
  const { categoryPage, sortFilter, localLanguage } = useSelector(
    (state: AppState) => ({
      categoryPage: state.categoryPage.categoryPage,
      searchRequest: state.categoryPage.searchRequest,
      sortFilter: state.sortFilter,
      localLanguage: state.homePage.language,
    }),
    shallowEqual,
  );

  const [breadcrumbList, setBreadcrumbList] = useState<any[]>([]);
  const router = useRouter();

  const t = useTranslation();

  const handleJumpToPrevPage = useCallback(
    (curPage: number) => {
      if (curPage <= 0) {
        return false;
      }
      if (curPage === 1) {
        return `/${language}/blog/${page?.url}`.replace(/\/\//g, '/');
      }

      return `?page=${curPage}`;
    },
    [language, page?.url],
  );

  const handleJumpToNextPage = useCallback(
    (page: number) => {
      const totalPage = Math.floor(categoryPage.total / 9) + (categoryPage.total % 9 > 0 ? 1 : 0);
      if (page > totalPage) {
        return false;
      }
      return `?page=${page}`;
    },
    [categoryPage],
  );

  const handleClickPage = useCallback(() => {
    localStorage.setItem('categoryFlag', 'onSort');
    localStorage.setItem('categorySortKey', JSON.stringify(sortFilter));
  }, [sortFilter]);

  const itemRender: PaginationProps['itemRender'] = (curpage: any, type, originalElement) => {
    if (type === 'prev') {
      return (
        // @ts-ignore
        <a rel="prev" href={handleJumpToPrevPage(curpage)}>
          <Image src="/blog/icon/Left.svg" alt={''} width={9} height={14} />
        </a>
      );
    }
    if (type === 'next') {
      return (
        // @ts-ignore
        <a rel="next" href={handleJumpToNextPage(curpage)}>
          <Image src="/blog/icon/Right.svg" alt={''} width={9} height={14} />
        </a>
      );
    }

    if (type === 'jump-prev') {
      return (
        <a rel="prev" href={`?page=${curpage}`}>
          <Image src="/blog/icon/JumpForwardArrow.png" alt={''} width={24} height={24} />
          <Image src="/blog/icon/JumpBackArrowDoubleLeft.png" alt={''} width={24} height={24} />
        </a>
      );
    }

    if (type === 'jump-next') {
      return (
        <a rel="next" href={`?page=${curpage}`}>
          <Image src="/blog/icon/JumpForwardArrow.png" alt={''} width={24} height={24} />
          <Image src="/blog/icon/JumpForwardArrowDouble.png" alt={''} width={24} height={24} />
        </a>
      );
    }

    return (
      <a href={curpage === 1 ? `/${language}/blog${page?.url}`.replace(/\/\//g, '/') : `?page=${curpage}`}>
        {originalElement}
      </a>
    );
  };

  const itemRenderOne: PaginationProps['itemRender'] = (page: any, type, originalElement) => {
    if (type === 'prev') {
      return <span className="Pagination_Prev"></span>;
    }
    if (type === 'next') {
      return <span className="Pagination_Next"></span>;
    }
    return originalElement;
  };

  const handleSearch = useCallback(
    async (pageNumber: number, fromSort: boolean = false, sortByKey: string = '') => {
      NProgress.start();
      const resSortKey = fromSort ? sortByKey : sortFilter.sortBy;
      let isFromSearch =
        localStorage.getItem('categoryFlag') === 'onSearch' || localStorage.getItem('categoryFlag') === 'onSort';
      if (isFromSearch) {
        localStorage.setItem('categoryFlag', '');
      }
      const newSortFilter = isFromSearch ? JSON.parse(localStorage.getItem('categorySortKey') as any) : sortFilter;
      const newResSortKey = isFromSearch ? newSortFilter.sortBy : resSortKey;
      await dispatch(changePage(pageNumber));
      await dispatch(
        getCategoryPageAction({
          ...newSortFilter,
          page: pageNumber,
          language: localLanguage.label,
          sortBy: newResSortKey,
          pageSize: 9,
        }),
      );
      await dispatch(changeIsOpen(false));
      await dispatch(changeTopicSelected(newSortFilter?.topicTagEntryIds));
      await dispatch(changeSortBy(newResSortKey));
      await dispatch(changeStartTimestamp(newSortFilter.startTimestamp));
      await dispatch(changeEndTimestamp(newSortFilter.endTimestamp));
      await dispatch(changeProductSelected(newSortFilter.prodTagEntryIds));
      NProgress.done();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [dispatch, localLanguage.label, sortFilter],
  );

  const handleChangePagination = async (pageNumber: number) => {
    await handleSearch(pageNumber);
  };

  useEffect(() => {
    const currentPage = Number(router?.query?.page);
    if (router?.query?.page === undefined && Object.keys(page)?.length > 0) {
      handleSearch(1);
    } else {
      if (Number.isNaN(currentPage) || currentPage < 0) {
        dispatch(
          changeCategoryPage({
            records: [],
            total: 57,
            page: 99,
            pageSize: 9,
            totalPage: 7,
          }),
        );
      } else {
        if (Object.keys(page)?.length > 0) {
          handleSearch(Number(currentPage));
        }
      }
    }
  }, []);

  useEffect(() => {
    setTimeout(() => {
      window?.dataLayer?.push({
        event: 'content_landing',
        author: '',
        description:
          page?.locale == 'zh-hk'
            ? page?.banner?.description?.substring(0, 20)
            : page?.banner?.description?.substring(0, 40),
        publish_time: page?.publish_details?.time,
        title: page?.title,
        subtitle: page?.banner?.subtitle,
        tags: page?.tags?.join('|'),
        pageURL: window.location.href,
        content_Group: category,
      });
    }, 1000);
  }, []);

  useEffect(() => {
    if (!Object.keys(page)?.length) {
      setTimeout(() => {
        const targetUrl = window.location.origin + (language == 'zh' ? '/zh/blog/' : '/en/blog/');
        router.replace(targetUrl);
      }, 3000);
    }
  }, []);

  const handleReset = async () => {
    NProgress.start();
    await dispatch(resetSortFilter({}));

    await dispatch(
      getCategoryPageAction({
        ...sortFilter,
        page: 1,
        topicTagEntryIds: [],
        prodTagEntryIds: [],
        language: localLanguage.label,
        sortBy: 'publishDate',
        pageSize: 9,
      }),
    );
    NProgress.done();
  };

  const handleSort = async (sort: string) => {
    if (router?.query?.page === undefined) {
      await handleSearch(1, true, sort);
    } else {
      localStorage.setItem('categoryFlag', 'onSort');
      localStorage.setItem('categorySortKey', JSON.stringify({ ...sortFilter, sort }));
      // let currentRouter = router.asPath;
      // currentRouter = currentRouter.replace(/\d/, '1');
      router.replace(`/${language}/blog/${page?.url}`.replace(/\/\//g, '/'));
    }
  };

  // 搜索并跳转到新页面
  const searchAndRedirectPage = useCallback(() => {
    if (router?.query?.page === undefined) {
      handleSearch(1);
    } else {
      localStorage.setItem('categoryFlag', 'onSearch');
      localStorage.setItem('categorySortKey', JSON.stringify(sortFilter));
      let currentRouter = router.asPath;
      currentRouter = currentRouter.replace(/\d/, '1');
      router.replace(`/${language}/blog/${page?.url}`.replace(/\/\//g, '/'));
    }
  }, [handleSearch, language, page?.url, router, sortFilter]);

  const categoryArticle = categoryPage.records || [];
  const articleLength = categoryArticle.length;

  useEffect(() => {
    const categoryUrl = page?.url;

    let list = [
      {
        name: language == 'zh' ? '首頁' : 'Home',
        link: language == 'zh' ? 'https://www.fwd.com.hk/' : 'https://www.fwd.com.hk/en/',
        title:
          language == 'zh' ? (
            <a
              href="https://www.fwd.com.hk/"
              target={'_blank'}
              onClick={() => hanldeGA('首頁', 'https://www.fwd.com.hk/')}
            >
              首頁
            </a>
          ) : (
            <a
              target={'_blank'}
              href="https://www.fwd.com.hk/en/"
              onClick={() => hanldeGA('Home', 'https://www.fwd.com.hk/en/')}
            >
              Home
            </a>
          ),
      },
      {
        name: language == 'zh' ? '網誌' : 'Blog',
        link: window.location.origin + (language == 'zh' ? '/zh/blog/' : '/en/blog/'),
        title:
          language == 'zh' ? (
            <a href="/zh/blog/" onClick={() => hanldeGA('網誌', '/zh/blog/')}>
              網誌
            </a>
          ) : (
            <a href="/en/blog/" onClick={() => hanldeGA('Blog', '/en/blog/')}>
              Blog
            </a>
          ),
      },
      {
        name: page.category_reference?.length > 0 ? page.category_reference[0].title : '',
        link:
          window.location.origin +
          (language == 'zh'
            ? `/zh/blog/${categoryUrl}`.replace(/\/\//g, '/')
            : `/en/blog/${categoryUrl}`.replace(/\/\//g, '/')),
        title: (
          <a
            href={
              language == 'zh'
                ? `/zh/blog/${categoryUrl}`.replace(/\/\//g, '/')
                : `/en/blog/${categoryUrl}`.replace(/\/\//g, '/')
            }
            onClick={() =>
              hanldeGA(
                category,
                language == 'zh'
                  ? `/zh/blog/${categoryUrl}`.replace(/\/\//g, '/')
                  : `/en/blog/${categoryUrl}`.replace(/\/\//g, '/'),
              )
            }
          >
            {category}
          </a>
        ),
      },
    ];

    setBreadcrumbList(list);
  }, []);

  const hanldeGA = (title: any, href: any) => {
    window?.dataLayer?.push({
      event: 'buttonClick',
      buttonTitle: 'blog_menu_bar',
      description: title,
      destinationUrl: href,
      pageURL: window.location.href,
      componentName: 'breadcrumb',
    });
  };

  const goToBlog = () => {
    breadcrumbList && breadcrumbList[1] && breadcrumbList[1].link ? router.push(breadcrumbList[1].link) : '';
  };

  return (
    <>
      {Object.keys(page)?.length && Object.keys(page)?.length > 0 ? (
        <div className={styles.Category}>
          <div className={styles.Category_Banner}>
            <div className={styles.Banner}>
              <div className={styles.BannerImage_PC}>
                <NextImage
                  loading="eager"
                  src={page.banner?.banner_desktop?.url || placeholder}
                  alt={page.banner?.banner_alt_text}
                  width={1366}
                  height={314.5}
                />
              </div>

              <div className={styles.BannerImage_M}>
                <NextImage
                  loading="eager"
                  src={page.banner?.banner_mobile?.url || placeholder}
                  alt={page.banner?.banner_alt_text}
                  width={390}
                  height={260}
                />
              </div>
            </div>
            <div className={styles.breadcrumb_wrapper}>
              <div className={styles.breadcrumb_content}>
                <Image
                  src="/blog/icon/ArrowLeftWhite.svg"
                  className={styles.arrowleft_icon}
                  alt={''}
                  width={18}
                  height={16}
                  onClick={goToBlog}
                />
                <Breadcrumb
                  separator={
                    <Image
                      src="/blog/icon/BreadIconWhite.svg"
                      className={styles.breadcrumb_icon}
                      alt={''}
                      width={16}
                      height={16}
                    />
                  }
                  className={styles.breadcrumb}
                  items={breadcrumbList}
                />
              </div>
            </div>
            <div className={styles.Category_Info_Wrapper}>
              <div className={styles.Category_Info}>
                <div>
                  <div className={styles.Category_Info_Title}>
                    <h1 className={styles.Banner_Title}>{page.title}</h1>
                  </div>
                  <p className={styles.Category_Info_Sub_Title}>{page.banner?.subtitle}</p>
                </div>
                <h2 className={styles.Category_Info_Description}>{page.banner?.description}</h2>
              </div>
            </div>
          </div>

          <div className={styles.Sorting_Filtering_Wrap}>
            <SortFilter
              tags=""
              category={category}
              onSearch={searchAndRedirectPage}
              onReset={() => handleReset()}
              onSort={handleSort}
              language={language}
            />
          </div>

          {articleLength > 2 ? (
            <div className={styles.Article_Box_Large}>
              {categoryArticle.map((record: any, k: number) =>
                k > 2 ? (
                  <LatestArticle
                    key={record.uid}
                    latestArticle={record}
                    isHide={true}
                    className={styles.Latest_Article}
                    lazyImage={true}
                    h3Title={true}
                  />
                ) : (
                  <LatestArticle
                    key={record.uid}
                    latestArticle={record}
                    isHide={true}
                    className={styles.Latest_Article}
                    lazyImage={false}
                    h3Title={true}
                  />
                ),
              )}
            </div>
          ) : articleLength === 2 ? (
            <div className={styles.Category_Article}>
              <div className={styles.Article_Box_Small}>
                {categoryArticle.map((record: any) => (
                  <LatestArticle
                    key={record.uid}
                    latestArticle={record}
                    isHide={true}
                    className={styles.Latest_Article_Few}
                    lazyImage={false}
                    h3Title={true}
                  />
                ))}
              </div>
            </div>
          ) : articleLength === 1 ? (
            <div className={styles.Article_Box_One}>
              {categoryArticle.map((record: any) => (
                <LatestArticle
                  key={record.uid}
                  latestArticle={record}
                  isHide={true}
                  className={styles.Latest_Article_One}
                  lazyImage={false}
                  h3Title={true}
                />
              ))}
            </div>
          ) : (
            <div className={styles.No_Result}>
              <div className={styles.Wrapper}>
                <NextImage
                  loading="eager"
                  src="/blog/icon/NoResult.svg"
                  className={styles.Icon}
                  alt={''}
                  width={100}
                  height={100}
                />
                {language === 'zh' ? (
                  <p className={styles.Content}>沒有搜尋結果</p>
                ) : (
                  <p className={styles.Content}>No results</p>
                )}
              </div>
            </div>
          )}

          <div
            className={classNames(styles.Pagination, language === 'en' ? styles.Pagination_EN : styles.Pagination_ZH)}
            onClick={handleClickPage}
          >
            {categoryPage && articleLength > 0 && categoryPage.totalPage && categoryPage.totalPage > 1 ? (
              <Pagination
                size="small"
                current={categoryPage && categoryPage.page}
                total={categoryPage && categoryPage.total}
                showSizeChanger={false}
                pageSize={9}
                onChange={handleChangePagination}
                itemRender={itemRender}
                showTitle={false}
              />
            ) : categoryPage && articleLength > 0 ? (
              <Pagination
                size="small"
                current={categoryPage && categoryPage.page}
                total={categoryPage && categoryPage.total}
                showSizeChanger={false}
                onChange={handleChangePagination}
                itemRender={itemRenderOne}
                showTitle={false}
                pageSize={9}
              />
            ) : (
              ''
            )}
          </div>

          {/* <Head>
        <>
          {breadcrumbList?.length > 0 ? (
            <script
              {...jsonLdScriptProps<BreadcrumbList>({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: breadcrumbList?.map((item: any, index: number) => {
                  return {
                    '@type': 'ListItem',
                    position: index + 1,
                    name: item.name,
                    item: item.link
                  };
                })
              })}
            />
          ) : (
            ''
          )}
        </>
      </Head> */}
        </div>
      ) : (
        <div className={styles.catgory_error_page}>
          <p>{language === 'zh' ? '您訪問的頁面不存在' : 'The page you are visiting does not exist'}</p>
        </div>
      )}
    </>
  );
});

export const getServerSideProps = wrapper.getServerSideProps(store => async (ctx: ServerContext) => {
  try {
    const language = (ctx.query.language as string) ?? null;
    const page = ctx.query?.page ?? null;
    const localLanguage = language === 'en' ? 'en-us' : 'zh-hk';
    let categoryDetail: any = {};
    const { category } = ctx.query;
    const isSvg = filterSvgPath(ctx.req?.url);
    let catgeoryPageData;
    let promotionBannerData;
    let popUpBannerData;

    // if (category === 'insurance') {
    //   ctx.res.writeHead(302, {
    //     Location:
    //       page && Number(page) > 1
    //         ? `${language ? `/${language}` : ''}` + `/blog/protection?page=${page}`
    //         : `${language ? `/${language}` : ''}` + `/blog/protection` // Specify the target route here
    //   });
    //   ctx.res.end();
    // }

    if (!isSvg) {
      const lang = language === 'en' ? 'en-us' : 'zh-hk';
      const pathname = '/blog/[category]';

      const [result1, result2, result3, result4, result5, result6, result7]: any[] = await Promise.all([
        getAllCategory(lang),
        getAllCategoryLanding(lang),
        getPromotionBanner(pathname, lang),
        getMultiplePopupBanner(pathname, lang),
        getHeaderAndFooter(HEADER_FOOTER_CONTENT_TYPE, headerAndFooterIncludeReference, lang),
        getCateTags(lang),
        getProductTags(lang),
      ]);

      changeDefaultData(store, result1, result2, result3, result4, result5);

      // save promotionBannerData & popupBannerData for new layout by Emma-2024.5.30
      promotionBannerData = result3?.data;
      popUpBannerData =
        Array.isArray(result4?.data) && Array.isArray(result4?.data[0]) && result4?.data[0].length > 0
          ? result4?.data[0][0]
          : {};

      const cateTags = Object.entries(result6?.data)?.map(([title, topics]) => ({ title, topics }));
      store.dispatch(changeCateTags(cateTags));
      store.dispatch(changeProductTags(result7?.data[0] || []));

      const { categoryPage } = store.getState();
      const { allCategoryLanding } = categoryPage;
      categoryDetail = allCategoryLanding.find((item: any) => item?.url === `/${category}/`);

      if (categoryDetail) {
        const categoryId = categoryDetail.uid;
        store.dispatch(changeCategoryId(categoryId));
        store.dispatch(changePageSize(9));

        const { data } = await getCategoryPage({ categoryId, pageSize: 9, language: lang });
        catgeoryPageData = data;
        if (data) {
          store.dispatch(changeCategoryPage(data));
        }
      }
    }

    const props = {
      page: categoryDetail || {},
      category,
      language,
      categoryPage: catgeoryPageData || {},
      // promotion banner and popup banner for new layout
      promotionBannerData: promotionBannerData || null,
      popUpBannerData: popUpBannerData || null,
    };
    info('blog/category props:', props);
    return {
      props: props,
    };
  } catch (e) {
    error('blog/category SSR fetch error:', e);
    return { notFound: true };
  }
});
